<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.time-off-balances-edit-dialog(
    :title="title"
    append-to-body
    :show="show"
    @close="close"
  )
    template(#corner)
      a.dialog-faq-link(
        target="_blank"
        title="Read the help article about this feature"
        href="https://support.hubstaff.com/importing-exporting-and-editing-time-off-balances/"
        rel="noopener noreferrer"
      ) ?

    .alert.alert-danger(v-if="baseError") {{ baseError }}
    template(v-if="!processed")
      ValidationProvider(
        v-slot="{ errors }"
        rules="required"
        name="file_picker"
        :model-value="file"
      )
        .form-group(:class="{ 'has-error': errors[0] }")
          label.control-label(for="file") File*
          AppFilePicker(
            ref="filefield"
            accept=".csv, .xls, .xlsx"
            @input="file = $event"
          )
          span.help-block {{ errors[0] }}
          span.accepted-formats Accepted file formats: .csv, .xls, .xlsx
          span
            a.btn(@click="spreadsheetTemplate") Download spreadsheet template
      ValidationProvider(
        v-slot="{ errors }"
        rules="required"
        name="reason"
        :model-value="reason"
      )
        .form-group.text(:class="{ 'has-error': errors[0] }")
          label.control-label(for="reason") Reason*
          textarea.form-control(
            v-model="reason"
            name="reason"
            rows="4"
            placeholder="Enter the reason you are adjusting the balance"
          )
          span.help-block {{ errors[0] }}
    template(v-else)
      .vertical-scroll-table
        table.table-hover.table-condensed.table-borderless.time-off-balances-table.table
          tbody.thead
            tr
              th User
              th Policy
              th Change
              th Old
              th New
          tbody.tbody
            tr(
              v-for="(balance, index) in sortedImportParams"
              :key="`${balance.user_name}-${index}`"
            )
              td {{ balance.user_name }}
              td {{ balance.policy_name }}
              td(:class="changeClasses(balance.change)") {{ balance.change }}
              td {{ balance.existing_balance }}
              td {{ balance.new_balance }}

      .form-group
        AppCheckbox(v-model="apply_accruals") Apply the starting annual accrual amount
    template(#footer)
      button.btn.btn-default(@click="closeOrGoBack") Cancel
      button.btn.btn-primary(
        :disabled="saving"
        @click="save"
      )
        | {{ saveButtonLabel }}
</template>
<script>
import { mapGetters } from 'vuex';
import { exportBook } from '~/utils/time_off_export_utils';
import { processFile } from '~/utils/time_off_import_utils';
export default {
  name: 'TimeOffBalancesImportDialog',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorMessage: 'There was an error when updating time off balances',
      successMessage: 'Time off balances updated',
      select2Options: {
        allowClear: false,
        multiple: false,
        width: '100%',
      },
      saving: false,
      apply_accruals: true,
      reason: '',
      file: '',
      importParams: [],
      processed: false,
      baseError: null,
    };
  },
  computed: {
    ...mapGetters(['timeOffPolicies', 'users', 'timeOffBalances', 'preImportSummaryPath', 'csvRowLimit']),
    title() {
      return this.processed ? 'Confirm balances import' : 'Import balances';
    },
    sortedImportParams() {
      return this.importParams.slice(0).sort((first, second) => first.user_ids[0] - second.user_ids[0]);
    },
    balanceAdjustments() {
      return this.importParams.map(({ user_ids: userIds, time_off_policy_id: timeOffPolicyId, amount }) => {
        return {
          user_ids: userIds,
          time_off_policy_id: timeOffPolicyId,
          amount,
          modify_balance: 'replace',
        };
      });
    },
    timeOffBalanceParams() {
      return {
        reason: this.reason,
        apply_accruals: this.apply_accruals,
        balance_adjustments: this.balanceAdjustments,
      };
    },
    saveButtonLabel() {
      return this.saving ? 'Importing...' : this.processed ? 'Confirm' : 'Import';
    },
  },
  created() {
    this.reset();
  },
  mounted() {
    this.$refs.dialogObserver.reset();
  },
  methods: {
    changeClasses(change) {
      if (change === '-') {
        return '';
      }
      if (change.startsWith('+')) {
        return 'add';
      }
      if (change.startsWith('-')) {
        return 'subtract';
      }
    },
    spreadsheetTemplate() {
      exportBook({}, 'csv');
    },
    closeOrGoBack() {
      if (this.processed) {
        this.reset();
        this.$refs.dialogObserver.reset();
      } else {
        this.close();
      }
    },
    close() {
      this.reset();
      this.$refs.dialogObserver.reset();
      this.$emit('close');
    },
    async save() {
      this.baseError = null;
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid) {
        if (!this.processed) {
          try {
            this.importParams = await processFile(this.file, this.preImportSummaryPath, this.csvRowLimit, true);
            this.processed = true;
          } catch (error) {
            this.$refs.filefield.reset();
            this.file = '';
            this.processed = false;
            alert(error.message);
          }
        } else {
          const success = this.success;
          const error = this.error;
          const fin = () => {
            this.saving = false;
          };

          this.saving = true;
          this.$store
            .dispatch('updateBalances', { params: this.timeOffBalanceParams })
            .then(success, error)
            .finally(fin);
        }
      }
    },
    success(response) {
      this.$appMessage(this.successMessage);
      this.$store.dispatch('loadData');
      this.close();
    },
    error(response) {
      this.processed = false;
      if (response.responseJSON) {
        if (response.responseJSON.alert) {
          this.$appMessage(response.responseJSON.alert);
        }
        this.baseError = response.responseJSON.error_message;
        this.$refs.dialogObserver.setErrors(response.responseJSON.errors);
      } else {
        this.$appMessage(this.errorMessage);
      }
    },
    reset() {
      this.$refs.filefield && this.$refs.filefield.reset();
      this.file = '';
      this.apply_accruals = true;
      this.reason = '';
      this.processed = false;
      this.saving = false;
      this.baseError = null;
    },
  },
};
</script>
